// Popup.tsx
// Universal modal/popup component using PortalBasic
import * as React from 'react'
import { ReactNode, useEffect } from 'react'
import { Portal } from './Portal'
import styles from './Popup.module.scss'
import clx from 'classnames'
import { FaIcon } from '@/lib/fa'
import { faXmark } from '@fortawesome/free-solid-svg-icons'
import { AppUiLayer } from './type'
import { layerClass } from '@/modules/shared/constants/layer'

type PopupProps = {
  open?: boolean
  onClose?: () => void
  children?: ReactNode
  closeOnBackdrop?: boolean // optional
  closeOnEscape?: boolean
  contentClassName?: string
  showCloseButton?: boolean
  /**
   * @deprecated Use `layer` instead.
   */
  zIndexClassName?: string
  layer?: AppUiLayer
}

export const Popup = ({
  open = true,
  onClose,
  children,
  closeOnBackdrop = true,
  closeOnEscape = true,
  contentClassName,
  showCloseButton = true,
  zIndexClassName = 'tw-z-9999',
  layer = 'modal',
}: PopupProps) => {
  const layerClassName = layerClass[layer].panel

  // Prevent body scroll when popup is open
  useEffect(() => {
    if (open) {
      const originalOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'

      return () => {
        document.body.style.overflow = originalOverflow
      }
    }
  }, [open])

  // Close on ESC
  useEffect(() => {
    if (!open) return

    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }

    if (closeOnEscape) {
      window.addEventListener('keydown', handler)
      return () => window.removeEventListener('keydown', handler)
    }
  }, [open, onClose, closeOnEscape])

  if (!open) return null

  return (
    <Portal layer={layer}>
      <div
        className={clx(styles.popupBackdrop, zIndexClassName, layerClassName)}
        onClick={closeOnBackdrop ? onClose : undefined}
      >
        <div
          className={clx(styles.popupContent, contentClassName)}
          onClick={(e) => e.stopPropagation()}
        >
          {showCloseButton && onClose && (
            <button
              className={styles.closeButton}
              onClick={onClose}
              aria-label="Close popup"
            >
              <FaIcon icon={faXmark} />
            </button>
          )}

          {children}
        </div>
      </div>
    </Portal>
  )
}
